/**
 * Impersonation — viewing the product as another user.
 *
 * Starting one mints a SEPARATE short-lived token whose subject is the target
 * and which carries the real actor as a claim. It deliberately has no refresh
 * token: impersonation expires rather than renewing, so a forgotten tab stops
 * being someone else. The server writes an audit row against both parties on
 * start and on stop.
 *
 * Who may do it is decided entirely by the API (rbac_app_role.can_impersonate
 * plus an organization-scope check). `getImpersonationStatus` exists so the UI
 * can avoid offering a control that could only ever return 403.
 */

import { get, post } from './client';

/** Whether this session is impersonating, and whether it may start one. */
export const getImpersonationStatus = () => get('/impersonation/status');

/**
 * The people this caller may impersonate — an organization's users for an org
 * admin, everyone for a platform admin.
 *
 * Its own endpoint rather than a filter on /users, because that list needs the
 * user-administration permission and a supervisor granted impersonation need
 * not hold it. Scoped by the same rules /start enforces, so nothing it returns
 * can come back 403.
 *
 * @param {{q?: string, limit?: number}} params
 * @returns {Promise<{items: Array<{id, email, username, name, org_name, roles}>, scope: 'platform'|'org'|'none', can_impersonate: boolean}>}
 */
export const getImpersonationCandidates = (params = {}) => get('/impersonation/candidates', params);

/**
 * Begin acting as `userId`.
 * @returns {Promise<{access_token: string, expires_in: number, impersonating: {user_id, email, name}, actor: {user_id, email}}>}
 */
export const startImpersonation = (userId) => post('/impersonation/start', { user_id: userId });

/**
 * End impersonation. Returns a fresh NORMAL token for the actor, so they land
 * back in their own session rather than at the login screen.
 * @returns {Promise<{access_token: string, user_id: string, email: string}>}
 */
export const stopImpersonation = () => post('/impersonation/stop', {});

export default { getImpersonationStatus, getImpersonationCandidates, startImpersonation, stopImpersonation };
